import { DataModelItemStatus } from '@flowx/designer/modules/ai/agents/designer/dictionary/designer.dictionary'
import { ProjectDataModelCatalogueEntry } from '@flowx/designer/modules/ai/conversation/project-data-model-catalogue'
import { DataModelDraft, DataModelDraftItem } from '@flowx/designer/modules/ai/types/ai.types'
import { DataModelAttributeType } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'

import { AiDmKeyRow, AiDmKeyRowSource, AiDmKeyRowStatus } from '../ai-dm-key-row.model'

type DesignerRow = AiDmKeyRow<DataModelDraftItem>

const baseRow = (item: DataModelDraftItem): DesignerRow => ({
  // A real, stable id is required so the modal never keys its maps off `undefined`.
  id: item.id || item.key,
  key: item.key,
  label: item.label,
  type: item.type,
  multiInstance: item.multiInstance,
  description: item.description,
  active: !item.inactive,
  parentId: item.parentDataModelId,
  status: AiDmKeyRowStatus.Created,
  source: AiDmKeyRowSource.Process,
  ref: item,
})

const toProcessRow = (
  item: DataModelDraftItem,
  status: AiDmKeyRow['status'],
  keysEditable: boolean
): DesignerRow => {
  // A key is project-sourced when it carries a reference of its own (a reused project type) or when
  // its path resolves through a project reference (a plain key nested under an instantiated type,
  // flagged during reconciliation). Either way it renders purple with a read-only key.
  const isProjectSourced = !!item.targetResourceDefinitionId || !!item.fromProjectDataModel
  return {
    ...baseRow(item),
    status,
    source: isProjectSourced ? AiDmKeyRowSource.Project : AiDmKeyRowSource.Process,
    ...(isProjectSourced || !keysEditable
      ? { keyEditable: false }
      : { keyAutocomplete: { componentIdentifier: item.templateConfigIdentifier } }),
  }
}

// Map a project/library reference item to a row, validating it against the catalogue fetched for this
// message. If the catalogue IS present but the referenced type (id + appId) isn't in it, the agent
// hallucinated the reference — demote it to a plain local key marked `unresolved` so the user must opt
// it out. If the catalogue is empty/absent we can't validate (the developer flow doesn't always send
// PROJECT_DATA_MODEL, and a designer fetch can fail), so we trust the agent's reference as-is rather
// than downgrading a real OBJECT reference to a STRING key.
const toReferenceRow = (
  item: DataModelDraftItem,
  catalogue: ProjectDataModelCatalogueEntry[]
): DesignerRow => {
  const hasCatalogue = catalogue.length > 0
  const foundInCatalogue = catalogue.some(
    (entry) =>
      entry.resourceDefinitionId === item.targetResourceDefinitionId && entry.appId === item.appId
  )

  if (hasCatalogue && !foundInCatalogue) {
    return {
      ...baseRow(item),
      id: item.id || `ref-${item.targetResourceDefinitionId}-${item.appId}`,
      type: DataModelAttributeType.STRING,
      status: AiDmKeyRowStatus.Created,
      source: AiDmKeyRowSource.Process,
      unresolved: true,
    }
  }

  return {
    ...baseRow(item),
    id: item.id || `ref-${item.targetResourceDefinitionId}-${item.appId}`,
    status: AiDmKeyRowStatus.Created,
    source: AiDmKeyRowSource.Project,
    keyEditable: false,
  }
}

export const dataModelDraftToRows = (
  draft: DataModelDraft | undefined,
  catalogue: ProjectDataModelCatalogueEntry[] = [],
  keysEditable = true
): DesignerRow[] => {
  if (!draft) {
    return []
  }

  return [
    ...(draft.new ?? []).map((item) => toProcessRow(item, AiDmKeyRowStatus.Created, keysEditable)),
    ...(draft.existing ?? []).map((item) =>
      toProcessRow(item, AiDmKeyRowStatus.Used, keysEditable)
    ),
    ...(draft.referenced ?? []).map((item) => toReferenceRow(item, catalogue)),
  ]
}

const toDraftItem = (row: AiDmKeyRow, state: DataModelItemStatus): DataModelDraftItem => {
  const original = row.ref
  const isReference = state === DataModelItemStatus.REFERENCED

  return {
    ...(original as DataModelDraftItem),
    key: row.key,
    label: row.label ?? row.key,
    type: row.type,
    description: row.description,
    inactive: !row.active,
    state,
    isMapped: state === DataModelItemStatus.MAPPED,
    // A demoted (no longer referenced) row must not carry stale reference fields downstream.
    ...(isReference
      ? {}
      : { targetResourceDefinitionId: undefined, appId: undefined, referenceAppInfo: undefined }),
  }
}

export const rowsToDataModelDraft = (rows: AiDmKeyRow[]): DataModelDraft =>
  rows.reduce<DataModelDraft>(
    (draft, row) => {
      if (row.source === AiDmKeyRowSource.Project && !row.unresolved) {
        draft.referenced.push(toDraftItem(row, DataModelItemStatus.REFERENCED))
      } else if (row.status === AiDmKeyRowStatus.Used) {
        draft.existing.push(toDraftItem(row, DataModelItemStatus.MAPPED))
      } else {
        draft.new.push(toDraftItem(row, DataModelItemStatus.CREATED))
      }
      return draft
    },
    { new: [], existing: [], referenced: [] }
  )
